import React, { useState } from 'react';
import { DailyPerformanceChart, MetricTogglePanel, TrinomicLossOverlay } from '../components/InverterTrendAnalysis';

const InverterTrendAnalysis: React.FC = () => {
  const [selectedInverter, setSelectedInverter] = useState('BLOCK-G1-KR1 (IN-01)');
  const [selectedPlant, setSelectedPlant] = useState('Torrent Saurya Urja 05');
  const [dateRange, setDateRange] = useState('2025-10-01_to_2025-10-10');
  const [visibleMetrics, setVisibleMetrics] = useState(['production', 'yield', 'pr']);

  const handleToggleMetric = (metric: string) => {
    setVisibleMetrics(prev => 
      prev.includes(metric) 
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  const inverters = [
    'BLOCK-G1-KR1 (IN-01)',
    'BLOCK-G1-KR1 (IN-02)',
    'BLOCK-G1-KR1 (IN-03)',
    'BLOCK-G1-KR1 (IN-04)',
    'BLOCK-G1-KR1 (IN-05)'
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Daily Yield vs Efficiency Analysis
        </h1>
        <p className="text-gray-600 mt-1">
          Track inverter performance trends and efficiency patterns over time
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plant
            </label>
            <select
              value={selectedPlant}
              onChange={(e) => setSelectedPlant(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Torrent Saurya Urja 05">Torrent Saurya Urja 05</option>
              <option value="GHCL 10 MW">GHCL 10 MW</option>
              <option value="Pollax 16 MW">Pollax 16 MW</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inverter ID
            </label>
            <select
              value={selectedInverter}
              onChange={(e) => setSelectedInverter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {inverters.map(inv => (
                <option key={inv} value={inv}>{inv}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="2025-10-01_to_2025-10-10">Oct 1-10, 2025</option>
              <option value="2025-10-11_to_2025-10-20">Oct 11-20, 2025</option>
              <option value="2025-10-01_to_2025-10-31">October 2025</option>
            </select>
          </div>
        </div>
      </div>

      {/* Metric Toggle Panel */}
      <MetricTogglePanel 
        visibleMetrics={visibleMetrics}
        onToggleMetric={handleToggleMetric}
      />

      {/* Performance Chart */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <DailyPerformanceChart
          inverterId={selectedInverter}
          dateRange={dateRange}
          plantId={selectedPlant}
          visibleMetrics={visibleMetrics}
        />
      </div>

      {/* Intelligence Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Performance Analysis</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm">Efficiency trending upward (+2.1% this week)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <span className="text-sm">PR deviation within acceptable range (±3%)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-sm">Production consistent with weather patterns</span>
            </div>
          </div>
        </div>

        <TrinomicLossOverlay
          inverterId={selectedInverter}
          date={dateRange.split('_to_')[0]}
          isVisible={true}
        />
      </div>
    </div>
  );
};

export default InverterTrendAnalysis;